/**
 * Discord OAuth Callback Handler
 * 
 * This component handles the OAuth callback from Discord.
 * It extracts the authorization code and state from the URL and sends it
 * back to the parent window via localStorage (since window.opener
 * can be null after cross-origin navigation in some browsers).
 * 
 * The state parameter is used for CSRF protection - it must be validated
 * by the parent window against the expected state before using the code.
 */

import { useEffect, useRef } from 'react';
import { authLogger } from '../utils/logger';

export function DiscordCallback() {
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double-processing (React strict mode or re-renders)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Get the authorization code and state from URL params
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error) {
      authLogger.error('Discord OAuth error', { error, errorDescription });
      // Store error in localStorage for parent to pick up
      localStorage.setItem('discord-oauth-result', JSON.stringify({
        type: 'discord-oauth-callback',
        error: errorDescription || error,
        timestamp: Date.now(),
      }));
      // Also try postMessage (works in some browsers)
      if (window.opener) {
        window.opener.postMessage({
          type: 'discord-oauth-callback',
          error: errorDescription || error,
        }, window.location.origin);
      }
      // Close this popup after a short delay
      setTimeout(() => window.close(), 1000);
      return;
    }

    if (code) {
      // Validate that we received a state parameter (required for CSRF protection)
      if (!state) {
        authLogger.error('Discord OAuth missing state parameter');
        localStorage.setItem('discord-oauth-result', JSON.stringify({
          type: 'discord-oauth-callback',
          error: 'Missing state parameter - possible CSRF attack',
          timestamp: Date.now(),
        }));
        if (window.opener) {
          window.opener.postMessage({
            type: 'discord-oauth-callback',
            error: 'Missing state parameter - possible CSRF attack',
          }, window.location.origin);
        }
        setTimeout(() => window.close(), 1000);
        return;
      }

      authLogger.debug('Discord OAuth code received with state');
      // Store code and state in localStorage for parent to pick up
      localStorage.setItem('discord-oauth-result', JSON.stringify({
        type: 'discord-oauth-callback',
        code,
        state,
        timestamp: Date.now(),
      }));
      // Also try postMessage (works in some browsers)
      if (window.opener) {
        window.opener.postMessage({
          type: 'discord-oauth-callback',
          code,
          state,
        }, window.location.origin);
        window.close();
      } else {
        authLogger.debug('No window.opener, using localStorage fallback');
        // Close after a brief delay to ensure localStorage is written
        setTimeout(() => window.close(), 500);
      }
    }
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: 'system-ui, sans-serif',
      backgroundColor: 'var(--color-white)',
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px color-mix(in srgb, var(--color-black) 10%, transparent)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔗</div>
        <h2 style={{ margin: '0 0 10px 0', color: 'var(--color-midnight)' }}>Connecting Discord...</h2>
        <p style={{ margin: 0, color: 'var(--color-slate-dark)' }}>This window will close automatically.</p>
      </div>
    </div>
  );
}
